import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { UPLOAD_BASE_URL } from '../config';
import { GET_MY_ORDERS } from '../graphql/operations';
import Footer from '../components/Footer';

const MyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const getOrderStep = (order: any): number => {
    if (!order) return 0;
    if (order.status === 'completed') return 5;
    if (order.jobCardStatus === 'ready') return 4;
    if (order.jobCardStatus === 'printing' || order.jobCardStatus === 'finishing') return 3;
    if (order.jobCardStatus === 'pending_setup') return 2;
    if (order.hasJobCard) return 2;
    if (order.status === 'confirmed' || order.status === 'paid' || order.paymentStatus === 'paid' || order.status === 'in_progress') return 1;
    return 0;
  };

  const steps = [
    { label: 'Received', icon: 'fas fa-receipt', desc: 'Order placed' },
    { label: 'Confirmed', icon: 'fas fa-check-circle', desc: 'Awaiting production' },
    { label: 'Job Prep', icon: 'fas fa-clipboard-list', desc: 'Ticket generated' },
    { label: 'Production', icon: 'fas fa-print', desc: 'Printing & finishing' },
    { label: 'Ready', icon: 'fas fa-box-open', desc: 'Ready for delivery' },
    { label: 'Delivered', icon: 'fas fa-handshake', desc: 'Completed' }
  ];

  const username = (localStorage.getItem('username') || 'Valued Customer').replace(/\b\w/g, c => c.toUpperCase());

  // Auth Guard: redirect to login if not authenticated; redirect to admin if admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) {
      navigate('/login');
    } else if (role === 'admin' || role === 'manager' || role === 'admin-manager') {
      navigate('/admin');
    }
  }, [navigate]);

  // Query my orders
  const { data, loading, error, refetch } = useQuery(GET_MY_ORDERS, {
    fetchPolicy: 'network-only',
  });

  const ordersList = data?.myOrders || [];

  // Metrics calculations
  const totalOrders = ordersList.length;
  const pendingOrders = ordersList.filter((o: any) => ['pending', 'confirmed', 'in_progress'].includes(o.status)).length;
  const completedOrders = ordersList.filter((o: any) => o.status === 'completed').length;

  // Total money spent on paid or confirmed/completed orders
  const totalSpent = ordersList
    .filter((o: any) => o.paymentStatus === 'paid' || ['confirmed', 'in_progress', 'completed'].includes(o.status))
    .reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);

  // Filtered orders list
  const filteredOrders = ordersList.filter((order: any) => {
    const searchLower = orderSearch.toLowerCase();
    const matchesSearch =
      order.orderId.toLowerCase().includes(searchLower) ||
      (order.items && order.items.some((item: any) => item.productName.toLowerCase().includes(searchLower)));

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning" text="dark" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>Pending</Badge>;
      case 'confirmed':
        return <Badge bg="primary" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>Confirmed</Badge>;
      case 'in_progress':
        return <Badge bg="info" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>In Progress</Badge>;
      case 'completed':
        return <Badge bg="success" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>Cancelled</Badge>;
      default:
        return <Badge bg="secondary" className="px-2.5 py-1.5 fw-semibold text-capitalize rounded-pill" style={{ fontSize: '11.5px' }}>{status}</Badge>;
    }
  };

  const getPaymentBadge = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'paid':
        return <Badge bg="success" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>Paid</Badge>;
      case 'failed':
        return <Badge bg="danger" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>Failed</Badge>;
      default:
        return <Badge bg="warning" text="dark" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '10px' }}>Pending</Badge>;
    }
  };

  const generateInvoiceHtml = (order: any) => {
    const companyName = 'Prathitha Digital Printers';
    const companyAddress = '123 Print Street, Mysore, Karnataka 570001';
    const companyPhone = '+91 12345 67890';
    const companyEmail = 'support@prathitha.com';
    const orderDate = new Date(Number(order.createdAt) ? Number(order.createdAt) : order.createdAt).toLocaleDateString();

    const itemsHtml = order.items && order.items.length > 0
      ? order.items.map((item: any) => {
        const optionLines = item.options?.map((opt: any) => `<div style="font-size:12px;color:#555;margin-top:4px;">${opt.key.replace(/([A-Z])/g, ' $1')}: <strong>${opt.value}</strong></div>`).join('') || '';
        return `
            <tr style="border-bottom:1px solid #e5e7eb;">
              <td style="padding:10px 8px; vertical-align:top;">${item.productName}${optionLines}</td>
              <td style="padding:10px 8px; text-align:center; vertical-align:top;">${item.quantity}</td>
              <td style="padding:10px 8px; text-align:right; vertical-align:top;">₹${Number(item.estimatedPrice || 0).toLocaleString('en-IN')}</td>
            </tr>
          `;
      }).join('')
      : '<tr><td colspan="3" style="padding:12px; text-align:center; color:#6b7280;">No items available.</td></tr>';

    const deliveryLabel = order.deliveryType === 'pickup' ? 'Store Pickup' : 'Home Delivery';
    const deliveryAddress = order.deliveryType === 'home' ? order.deliveryAddress : 'Pickup from store';
    const paymentStatus = order.paymentStatus ? order.paymentStatus.toUpperCase() : 'PENDING';

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice - ${order.orderId}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111827; margin: 0; padding: 24px; background: #f8fafc; }
            .invoice-shell { max-width: 900px; margin: 0 auto; background: #ffffff; padding: 32px; border-radius: 16px; box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08); }
            .brand { color: #0f172a; font-size: 28px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 8px; }
            .meta { color: #475569; font-size: 14px; margin-top: 4px; }
            .section-title { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #0f172a; margin: 28px 0 12px; }
            .details-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
            .details-block { background: #f1f5f9; padding: 18px; border-radius: 14px; }
            .details-block strong { display: block; margin-bottom: 6px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { text-align: left; padding: 12px 8px; }
            th { background: #e2e8f0; color: #0f172a; font-size: 13px; }
            td { font-size: 13px; color: #334155; }
            .total-row td { font-size: 15px; font-weight: 700; border-top: 2px solid #e2e8f0; }
            .footer-note { margin-top: 24px; color: #475569; font-size: 12px; line-height: 1.6; }
            @media print { body { background: #fff; padding: 0; } .invoice-shell { box-shadow: none; border-radius: 0; padding: 0; } .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <div class="invoice-shell">
            <div class="brand">${companyName}</div>
            <div class="meta">Invoice for order <strong>${order.orderId}</strong> • Generated on ${new Date().toLocaleDateString()}</div>
            <div class="details-grid">
              <div class="details-block">
                <strong>Customer</strong>
                <div>${order.customerName || 'Valued Customer'}</div>
                <div>${order.customerEmail || '—'}</div>
                <div>${order.customerPhone || '—'}</div>
              </div>
              <div class="details-block">
                <strong>Order Details</strong>
                <div>Order ID: ${order.orderId}</div>
                <div>Order Date: ${orderDate}</div>
                <div>Status: ${order.status || 'N/A'}</div>
              </div>
              <div class="details-block">
                <strong>Payment</strong>
                <div>Payment Status: ${paymentStatus}</div>
                <div>Total Paid: ₹${Number(order.totalAmount || 0).toLocaleString('en-IN')}</div>
                <div>${order.razorpayOrderId ? `Razorpay Ref: ${order.razorpayOrderId}` : ''}</div>
              </div>
              <div class="details-block">
                <strong>Delivery</strong>
                <div>${deliveryLabel}</div>
                <div style="white-space:pre-wrap;">${deliveryAddress || ''}</div>
              </div>
            </div>
            <div class="section-title">Itemized Summary</div>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th style="width:100px; text-align:center;">Qty</th>
                  <th style="width:150px; text-align:right;">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
                <tr class="total-row">
                  <td colspan="2">Total</td>
                  <td style="text-align:right;">₹${Number(order.totalAmount || 0).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
            <div class="footer-note">
              ${companyName} • ${companyAddress} • ${companyPhone} • ${companyEmail}
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const printInvoice = (order: any) => {
    const invoiceHtml = generateInvoiceHtml(order);
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      window.alert('Unable to open invoice preview. Please allow popups for this site.');
      return;
    }

    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
    window.location.reload();
  };

  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="flex-grow-1 py-5">
        <Container>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <span className="text-primary fw-bold text-uppercase tracking-wider small" style={{ letterSpacing: '1px' }}>Customer Portal</span>
              <h2 className="fw-bold text-dark mb-1">Hello, {username}!</h2>
              <p className="text-muted mb-0" style={{ fontSize: '13.5px' }}>Here is your print order history and status tracking.</p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => refetch()}
                className="d-flex align-items-center gap-2 fw-semibold px-3 py-2 bg-white shadow-sm border border-light"
                style={{ borderRadius: '8px', fontSize: '13.5px' }}
                title="Refresh Order List"
              >
                <i className="fas fa-sync-alt"></i> Refresh
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleLogout}
                className="d-flex align-items-center gap-2 fw-semibold px-3 py-2"
                style={{ borderRadius: '8px', fontSize: '13.5px' }}
              >
                <i className="fas fa-sign-out-alt"></i> Logout
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          <Row className="g-3 mb-5">
            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light border text-primary rounded-3 p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="fas fa-shopping-bag" style={{ fontSize: '20px' }}></i>
                  </div>
                  <div>
                    <span className="text-muted d-block" style={{ fontSize: '12px', fontWeight: 500 }}>Total Orders</span>
                    <h3 className="fw-bold text-dark mb-0">{totalOrders}</h3>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light border text-warning rounded-3 p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="fas fa-clock" style={{ fontSize: '20px' }}></i>
                  </div>
                  <div>
                    <span className="text-muted d-block" style={{ fontSize: '12px', fontWeight: 500 }}>Active Orders</span>
                    <h3 className="fw-bold text-dark mb-0">{pendingOrders}</h3>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light border text-success rounded-3 p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="fas fa-check-double" style={{ fontSize: '20px' }}></i>
                  </div>
                  <div>
                    <span className="text-muted d-block" style={{ fontSize: '12px', fontWeight: 500 }}>Completed</span>
                    <h3 className="fw-bold text-dark mb-0">{completedOrders}</h3>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light border text-pink rounded-3 p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', color: 'var(--pink)' }}>
                    <i className="fas fa-wallet" style={{ fontSize: '20px' }}></i>
                  </div>
                  <div>
                    <span className="text-muted d-block" style={{ fontSize: '12px', fontWeight: 500 }}>Total Spent</span>
                    <h3 className="fw-bold text-dark mb-0">₹{totalSpent.toLocaleString('en-IN')}</h3>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Orders Section */}
          <Card className="border-0 shadow-sm rounded-4 bg-white p-4" style={{ minHeight: '400px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
              <h4 className="fw-bold text-dark mb-0">My Printing Orders</h4>

              <div className="d-flex gap-2 flex-wrap">
                <Form.Control
                  type="text"
                  placeholder="Search order items..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  style={{ maxWidth: '280px', borderRadius: '8px', fontSize: '13px' }}
                />
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: '160px', borderRadius: '8px', fontSize: '13px' }}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5 text-muted my-auto">
                <Spinner animation="border" variant="primary" className="mb-2" />
                <p>Retrieving your orders...</p>
              </div>
            ) : error ? (
              <Alert variant="danger" className="text-center border-0 rounded-3 my-auto py-4">
                <i className="fas fa-exclamation-triangle mb-2" style={{ fontSize: '24px' }}></i>
                <div>Failed to load order history: {error.message}</div>
              </Alert>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center my-auto py-5 text-muted">
                <i className="fas fa-shopping-basket mb-3" style={{ fontSize: '48px', opacity: 0.5 }}></i>
                <h5 className="fw-bold text-dark">No orders found</h5>
                <p className="small">You haven't placed any orders matching the criteria yet.</p>
                {/* <Link to="/order" className="btn btn-primary-custom py-2 px-4 mt-3">
                  <i className="fas fa-print me-1"></i> Order Now
                </Link> */}
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                  <thead className="table-light">
                    <tr style={{ fontSize: '12px', color: '#64748b' }}>
                      <th className="border-0 py-3">Order ID</th>
                      <th className="border-0 py-3">Products</th>
                      <th className="border-0 py-3 text-center">Items Count</th>
                      <th className="border-0 py-3">Price</th>
                      <th className="border-0 py-3">Order Date</th>
                      <th className="border-0 py-3 text-center">Status</th>
                      <th className="border-0 py-3 text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order: any) => (
                      <tr key={order.id} className="bg-light-row" style={{ fontSize: '13.5px' }}>
                        <td className="fw-semibold text-dark font-monospace py-3">{order.orderId}</td>
                        <td>
                          {order.items && order.items.length > 0
                            ? `${order.items[0].productName}${order.items.length > 1 ? ` + ${order.items.length - 1} more` : ''}`
                            : 'No product details'}
                        </td>
                        <td className="text-center">
                          {order.items && order.items.length > 0
                            ? order.items.reduce((acc: number, item: any) => acc + item.quantity, 0)
                            : 0}
                        </td>
                        <td className="fw-bold text-dark">
                          ₹{(order.totalAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td>{new Date(Number(order.createdAt) ? Number(order.createdAt) : order.createdAt).toLocaleDateString()}</td>
                        <td className="text-center">
                          <div>{getStatusBadge(order.status)}</div>
                          <div className="mt-1">{getPaymentBadge(order.paymentStatus)}</div>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="fw-semibold px-3"
                              style={{ borderRadius: '6px' }}
                              onClick={() => printInvoice(order)}
                            >
                              Invoice
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="fw-semibold px-3"
                              style={{ borderRadius: '6px' }}
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowOrderModal(true);
                              }}
                            >
                              View Specs
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </Container>
      </div>

      {/* DETAIL MODAL */}
      {selectedOrder && (
        <Modal
          show={showOrderModal}
          onHide={() => setShowOrderModal(false)}
          size="lg"
          centered
          className="customer-order-modal"
        >
          <Modal.Header closeButton className="border-bottom-0 pb-0">
            <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
              Order Specifications <span className="text-primary font-monospace" style={{ fontSize: '16px' }}>({selectedOrder.orderId})</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2">
            {/* Real-time Order Status Tracker */}
            {selectedOrder.status === 'cancelled' ? (
              <Alert variant="danger" className="border-0 rounded-4 p-3 d-flex align-items-center gap-3 mb-4">
                <i className="fas fa-times-circle text-danger" style={{ fontSize: '28px' }}></i>
                <div>
                  <h6 className="fw-bold mb-1 text-danger">Order Cancelled</h6>
                  <p className="mb-0 small text-muted">This order was cancelled and will not be processed further.</p>
                </div>
              </Alert>
            ) : (
              <div className="order-stepper-container mb-4">
                <h6 className="fw-bold text-dark mb-3">Live Order Tracking</h6>
                <div
                  className="stepper-wrapper"
                  style={{ '--progress-percent': `${(getOrderStep(selectedOrder) / 5) * 100}%` } as React.CSSProperties}
                >
                  <div className="stepper-progress-line">
                    <div className="stepper-progress-line-fill"></div>
                  </div>
                  {steps.map((step, idx) => {
                    const currentStep = getOrderStep(selectedOrder);
                    const isCompleted = idx < currentStep;
                    const isActive = idx === currentStep;

                    return (
                      <div
                        key={idx}
                        className={`stepper-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                      >
                        <div className="step-icon-wrapper">
                          {isCompleted ? (
                            <i className="fas fa-check"></i>
                          ) : (
                            <i className={step.icon}></i>
                          )}
                        </div>
                        <div className="step-content">
                          <div className="step-title">{step.label}</div>
                          <div className="step-subtitle">{step.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Additional production details once Job Card is created */}
                {selectedOrder.hasJobCard && (
                  <div className="d-flex justify-content-between align-items-center mt-3 p-3 bg-white rounded-3 border border-light flex-wrap gap-3" style={{ fontSize: '12.5px' }}>
                    <div>
                      <span className="text-muted">Job Card:</span>{' '}
                      <strong className="font-monospace text-dark">{selectedOrder.jobCardId}</strong>
                    </div>
                    {selectedOrder.jobCardOperator && (
                      <div>
                        <span className="text-muted">Operator:</span>{' '}
                        <strong className="text-dark">{selectedOrder.jobCardOperator}</strong>
                      </div>
                    )}
                    {selectedOrder.jobCardMachine && (
                      <div>
                        <span className="text-muted">Machine:</span>{' '}
                        <strong className="text-dark">{selectedOrder.jobCardMachine}</strong>
                      </div>
                    )}
                    <div>
                      <span className="text-muted">Production Stage:</span>{' '}
                      <Badge bg="info" className="text-capitalize px-2.5 py-1 rounded-pill" style={{ fontSize: '11px' }}>
                        {selectedOrder.jobCardStatus?.replace('_', ' ') || 'Job Setup'}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Row className="g-4">
              {/* Product list */}
              <Col xs={12}>
                <div className="bg-light p-4 rounded-4 border border-light mb-1">
                  <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Order Items</h5>
                  <div className="table-responsive">
                    <Table hover size="sm" className="bg-white rounded-3 overflow-hidden border mb-0">
                      <thead className="table-light">
                        <tr style={{ fontSize: '11.5px', color: '#64748b' }}>
                          <th className="py-2 px-3">Item / Specifications</th>
                          <th className="py-2 text-center" style={{ width: '80px' }}>Qty</th>
                          <th className="py-2 text-center" style={{ width: '120px' }}>Subtotal</th>
                          <th className="py-2 text-end px-3" style={{ width: '150px' }}>Design File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item: any, idx: number) => (
                            <tr key={idx} style={{ fontSize: '12.5px' }}>
                              <td className="py-2 px-3">
                                <div className="fw-bold text-dark">{item.productName}</div>
                                <div className="text-muted small mt-1" style={{ fontSize: '11px', lineHeight: '1.3' }}>
                                  {item.options.map((opt: any) => (
                                    <div key={opt.key} className="text-capitalize">
                                      {opt.key.replace(/([A-Z])/g, ' $1')}: <span className="fw-semibold text-dark">{opt.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td className="py-2 text-center align-middle">{item.quantity}</td>
                              <td className="py-2 text-center align-middle fw-bold text-dark">₹{item.estimatedPrice.toLocaleString('en-IN')}</td>
                              <td className="py-2 text-end align-middle px-3">
                                {item.designFileName ? (
                                  item.designFileName.startsWith('{') ? (
                                    (() => {
                                      try {
                                        const parsed = JSON.parse(item.designFileName);
                                        return (
                                          <div className="d-flex flex-column align-items-end gap-1">
                                            {parsed.front && (
                                              <a
                                                href={`${UPLOAD_BASE_URL}/${parsed.front}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                                style={{ borderRadius: '4px', fontSize: '10px', minWidth: '65px' }}
                                                download
                                              >
                                                <i className="fas fa-download"></i> Front
                                              </a>
                                            )}
                                            {parsed.back && (
                                              <a
                                                href={`${UPLOAD_BASE_URL}/${parsed.back}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                                style={{ borderRadius: '4px', fontSize: '10px', minWidth: '65px' }}
                                                download
                                              >
                                                <i className="fas fa-download"></i> Back
                                              </a>
                                            )}
                                          </div>
                                        );
                                      } catch (_) {
                                        return null;
                                      }
                                    })()
                                  ) : (
                                    <a
                                      href={`${UPLOAD_BASE_URL}/${item.designFileName}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                      style={{ borderRadius: '4px', fontSize: '11px' }}
                                      download
                                    >
                                      <i className="fas fa-download"></i> View File
                                    </a>
                                  )
                                ) : (
                                  <span className="text-muted small">No file attached</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr style={{ fontSize: '12.5px' }}>
                            <td colSpan={4} className="text-center text-muted py-3">No item specifications available.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>

              {/* Delivery info */}
              <Col md={6}>
                <div className="bg-light p-4 rounded-4 border border-light h-100">
                  <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Delivery & Shipping</h5>
                  <Row className="mb-2">
                    <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Method:</Col>
                    <Col xs={7} className="fw-semibold text-dark text-capitalize" style={{ fontSize: '13px' }}>
                      {selectedOrder.deliveryType === 'pickup' ? 'Store Pickup' : 'Home Delivery'}
                    </Col>
                  </Row>
                  {selectedOrder.deliveryType === 'home' && (
                    <Row className="mb-2">
                      <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Address:</Col>
                      <Col xs={7} className="text-dark" style={{ fontSize: '12.5px', lineHeight: '1.4' }}>{selectedOrder.deliveryAddress}</Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Contact Phone:</Col>
                    <Col xs={7} className="text-dark" style={{ fontSize: '13px' }}>{selectedOrder.customerPhone}</Col>
                  </Row>

                  {selectedOrder.notes && (
                    <div className="mt-3 bg-white p-2 rounded-3 border border-light" style={{ fontSize: '12px' }}>
                      <strong>Your Instructions:</strong>
                      <p className="mb-0 text-muted mt-1" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>
              </Col>

              {/* Payment info */}
              <Col md={6}>
                <div className="bg-light p-4 rounded-4 border border-light h-100">
                  <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Billing & Payment</h5>
                  <Row className="mb-2">
                    <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Order Status:</Col>
                    <Col xs={7} className="fw-semibold" style={{ fontSize: '13px' }}>
                      {getStatusBadge(selectedOrder.status)}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Payment Status:</Col>
                    <Col xs={7} className="fw-semibold" style={{ fontSize: '13px' }}>
                      {getPaymentBadge(selectedOrder.paymentStatus)}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Amount Paid:</Col>
                    <Col xs={7} className="fw-bold text-primary" style={{ fontSize: '15px' }}>
                      ₹{selectedOrder.totalAmount?.toLocaleString('en-IN')}
                    </Col>
                  </Row>

                  {selectedOrder.razorpayOrderId && (
                    <div className="mt-3 border-top pt-2" style={{ fontSize: '11px' }}>
                      <div className="text-muted mb-1"><strong>Razorpay Order Reference:</strong></div>
                      <div className="font-monospace bg-white border p-1 rounded text-dark text-truncate">{selectedOrder.razorpayOrderId}</div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Help Call to Action */}
            <div className="mt-4 bg-light p-3 rounded-4 border border-light text-center">
              <span className="small text-muted">
                Need help with this order? Contact support at <a href="mailto:support@prathitha.com" className="text-decoration-none fw-semibold">support@prathitha.com</a> or message us.
              </span>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button variant="outline-primary" onClick={() => selectedOrder && printInvoice(selectedOrder)} style={{ borderRadius: '8px', fontSize: '13px' }}>
              <i className="fas fa-file-invoice me-2"></i> Print Invoice
            </Button>
            <Button variant="secondary" onClick={() => setShowOrderModal(false)} style={{ borderRadius: '8px', fontSize: '13px' }}>
              Close Window
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Footer />
    </div>
  );
};

export default MyOrdersPage;
